import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { companiesApi } from "../companies/api";
import { CompanyProfileFields, splitJobTypes } from "../companies/CompanyFormFields";
import { useIndustryOptions } from "../industries/api";
import { Button, Card, Field, Input, PageTitle } from "../../components/ui";
import { ApiError } from "../../lib/api";

interface FormValues {
  name: string;
  area: string;
  industryCode: string;
  jobTypesText: string;
  description: string;
  capacity: number;
  conditions: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  latitude?: number | null;
  longitude?: number | null;
  isPublic: boolean;
  loginEmail?: string;
  password?: string;
}

export function AdminCompanyCreatePage() {
  const qc = useQueryClient();
  const industryQuery = useIndustryOptions();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { isPublic: true, conditions: "", industryCode: "" } });

  const mutation = useMutation({
    mutationFn: (v: FormValues) =>
      companiesApi.adminCreate({
        name: v.name,
        area: v.area,
        industryCode: v.industryCode,
        jobTypes: splitJobTypes(v.jobTypesText),
        description: v.description,
        capacity: Number(v.capacity),
        conditions: v.conditions ?? "",
        contactName: v.contactName,
        contactEmail: v.contactEmail,
        contactPhone: v.contactPhone,
        latitude: v.latitude ?? null,
        longitude: v.longitude ?? null,
        isPublic: v.isPublic,
        loginEmail: v.loginEmail || undefined,
        password: v.password || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin"] });
      reset({ isPublic: true, conditions: "", industryCode: "" });
    },
  });

  const apiErr = mutation.error instanceof ApiError ? mutation.error.message : null;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <PageTitle
        title="企業を代理登録"
        subtitle="タウンマネジメントが企業に代わって登録します（審査済み扱い）。"
      />
      <Card>
        <form className="space-y-4" onSubmit={handleSubmit((v) => mutation.mutate(v))}>
          <CompanyProfileFields
            register={register}
            errors={errors}
            industry={{
              isLoading: industryQuery.isLoading,
              isError: industryQuery.isError,
              data: industryQuery.data,
              refetch: () => void industryQuery.refetch(),
            }}
            location={{
              value: { latitude: watch("latitude") ?? null, longitude: watch("longitude") ?? null },
              onChange: (v) => {
                setValue("latitude", v?.latitude ?? null);
                setValue("longitude", v?.longitude ?? null);
              },
            }}
          />
          <label className="flex items-center gap-2 text-sm font-bold text-slate-600">
            <input type="checkbox" {...register("isPublic")} className="h-4 w-4" />
            登録後すぐに公開する
          </label>
          <div className="border-t border-sky-base/60 pt-4">
            <p className="mb-2 text-sm font-bold text-slate-600">
              ログインアカウント（任意・企業がログインする場合のみ）
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="ログイン用メール" error={errors.loginEmail?.message}>
                <Input type="email" {...register("loginEmail")} />
              </Field>
              <Field label="パスワード（12文字以上）" error={errors.password?.message}>
                <Input
                  type="password"
                  {...register("password", {
                    minLength: { value: 12, message: "12文字以上にしてください" },
                  })}
                />
              </Field>
            </div>
          </div>
          {apiErr && <p className="text-sm font-bold text-rose-500">{apiErr}</p>}
          {mutation.isSuccess && (
            <p className="text-sm font-bold text-emerald-600">登録しました。</p>
          )}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={
                mutation.isPending ||
                industryQuery.isLoading ||
                industryQuery.isError ||
                industryQuery.data?.length === 0 // 業種0件は送信 disabled（§3.3）
              }
            >
              代理登録する
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
